import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SkeletonCard() {
  return (
    <div className="border border-zinc-200 dark:border-zinc-800 p-5 animate-pulse">
      <div className="h-5 w-32 rounded bg-zinc-200 dark:bg-zinc-800 mb-4" />
      <div className="h-3 w-full rounded bg-zinc-100 dark:bg-zinc-800/50 mb-3" />
      <div className="h-3 w-2/3 rounded bg-zinc-100 dark:bg-zinc-800/50 mb-3" />
      <div className="h-3 w-1/2 rounded bg-zinc-100 dark:bg-zinc-800/50" />
    </div>
  );
}

function ComplianceBar({ compliant, borderline, non_compliant, unverified, total }) {
  if (total === 0) return null;
  const cPct = (compliant / total) * 100;
  const bPct = (borderline / total) * 100;
  const nPct = (non_compliant / total) * 100;
  const uPct = (unverified / total) * 100;

  return (
    <div className="w-full h-2 flex rounded-sm overflow-hidden" data-testid="compliance-bar">
      {cPct > 0 && <div style={{ width: `${cPct}%` }} className="bg-emerald-500" />}
      {bPct > 0 && <div style={{ width: `${bPct}%` }} className="bg-amber-500" />}
      {nPct > 0 && <div style={{ width: `${nPct}%` }} className="bg-red-500" />}
      {uPct > 0 && <div style={{ width: `${uPct}%` }} className="bg-zinc-500" />}
    </div>
  );
}

function RateColor({ rate }) {
  if (rate > 50) return "text-emerald-500";
  if (rate >= 25) return "text-amber-500";
  return "text-red-500";
}

function BadgeColor({ color }) {
  if (color === "green") return "bg-emerald-500/10 text-emerald-500 border-emerald-500/20";
  if (color === "amber") return "bg-amber-500/10 text-amber-500 border-amber-500/20";
  return "bg-red-500/10 text-red-500 border-red-500/20";
}

export default function SectorAnalytics({ onSelectStock }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("rate_desc");

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sectors/analytics`);
      setData(res.data);
    } catch { /* */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sorted = data?.sectors ? [...data.sectors].sort((a, b) => {
    if (sortBy === "rate_desc") return b.compliance_rate - a.compliance_rate;
    if (sortBy === "rate_asc") return a.compliance_rate - b.compliance_rate;
    return b.total - a.total;
  }) : [];

  const summary = data?.summary;

  return (
    <div data-testid="sector-analytics">
      {/* Summary bar */}
      {summary && !loading && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px mb-6 border border-zinc-200 dark:border-zinc-800 overflow-hidden" data-testid="sector-summary">
          <div className="p-4 bg-white dark:bg-zinc-950">
            <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 dark:text-zinc-500 mb-1">Most Halal Sector</p>
            <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400" data-testid="most-halal-sector">
              {summary.most_halal?.sector || "—"}
            </p>
            <p className="text-[11px] font-mono text-emerald-500">{summary.most_halal?.rate || 0}% compliant</p>
          </div>
          <div className="p-4 bg-white dark:bg-zinc-950">
            <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 dark:text-zinc-500 mb-1">Least Halal Sector</p>
            <p className="text-sm font-semibold text-red-600 dark:text-red-400" data-testid="least-halal-sector">
              {summary.least_halal?.sector || "—"}
            </p>
            <p className="text-[11px] font-mono text-red-500">{summary.least_halal?.rate || 0}% compliant</p>
          </div>
          <div className="p-4 bg-white dark:bg-zinc-950">
            <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 dark:text-zinc-500 mb-1">Overall Market Compliance</p>
            <p className={`text-sm font-semibold ${RateColor({ rate: summary.overall_compliance_rate })}`} data-testid="overall-rate">
              {summary.overall_compliance_rate}%
            </p>
            <p className="text-[11px] font-mono text-zinc-500">{summary.total_stocks} stocks across {summary.total_sectors} sectors</p>
          </div>
        </div>
      )}

      {/* Sort control */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs font-mono text-zinc-500 dark:text-zinc-400">
          {loading ? "Loading sectors..." : `${sorted.length} sectors`}
        </p>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="text-[11px] font-mono bg-transparent border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 rounded px-2 py-1 outline-none"
          data-testid="sector-sort"
        >
          <option value="rate_desc">Compliance: High to Low</option>
          <option value="rate_asc">Compliance: Low to High</option>
          <option value="count">Most Stocks</option>
        </select>
      </div>

      {/* Skeleton loading */}
      {loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      )}

      {/* Sector cards */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {sorted.map(sec => (
            <div
              key={sec.sector}
              className="border border-zinc-200 dark:border-zinc-800 p-5 hover:border-zinc-300 dark:hover:border-zinc-700 transition-colors"
              data-testid={`sector-card-${sec.sector.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {/* Header row */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-base font-bold text-zinc-900 dark:text-zinc-100">{sec.sector}</h3>
                  <p className="text-[11px] font-mono text-zinc-400 dark:text-zinc-500 mt-0.5">
                    {sec.total} stock{sec.total !== 1 ? "s" : ""} screened
                  </p>
                </div>
                <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border rounded ${BadgeColor({ color: sec.badge_color })}`}>
                  {sec.badge}
                </span>
              </div>

              {/* Compliance bar */}
              <ComplianceBar {...sec} />

              {/* Counts */}
              <div className="flex items-center gap-3 mt-2 mb-3">
                <span className="text-[10px] font-mono text-emerald-500">{sec.compliant} Compliant</span>
                {sec.borderline > 0 && <span className="text-[10px] font-mono text-amber-500">{sec.borderline} Borderline</span>}
                <span className="text-[10px] font-mono text-red-500">{sec.non_compliant} Non-Compliant</span>
                {sec.unverified > 0 && <span className="text-[10px] font-mono text-zinc-500">{sec.unverified} Unverified</span>}
              </div>

              {/* Compliance rate */}
              <div className="flex items-baseline gap-1 mb-4">
                <span className={`text-2xl font-bold font-mono ${RateColor({ rate: sec.compliance_rate })}`}>
                  {sec.compliance_rate}%
                </span>
                <span className="text-[11px] text-zinc-400 dark:text-zinc-500">Halal</span>
              </div>

              {sec.limited_data ? (
                <p className="text-[11px] font-mono text-zinc-400 dark:text-zinc-600 italic mb-3">
                  Limited data — more stocks coming soon
                </p>
              ) : (
                <>
                  {/* Average ratios */}
                  <div className="grid grid-cols-3 gap-2 mb-4 py-3 border-t border-b border-zinc-100 dark:border-zinc-800/50">
                    <div>
                      <p className="text-[9px] font-mono uppercase text-zinc-400 dark:text-zinc-500 mb-0.5">Avg Debt/MCap</p>
                      <p className="text-xs font-mono text-zinc-700 dark:text-zinc-300">
                        {sec.avg_debt_ratio != null ? `${sec.avg_debt_ratio}%` : "N/A"}
                        <span className="text-zinc-400 dark:text-zinc-600"> vs 33%</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] font-mono uppercase text-zinc-400 dark:text-zinc-500 mb-0.5">Avg Int/Rev</p>
                      <p className="text-xs font-mono text-zinc-700 dark:text-zinc-300">
                        {sec.avg_interest_ratio != null ? `${sec.avg_interest_ratio}%` : "N/A"}
                        <span className="text-zinc-400 dark:text-zinc-600"> vs 5%</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] font-mono uppercase text-zinc-400 dark:text-zinc-500 mb-0.5">Avg Recv/Assets</p>
                      <p className="text-xs font-mono text-zinc-700 dark:text-zinc-300">
                        {sec.avg_receivable_ratio != null ? `${sec.avg_receivable_ratio}%` : "N/A"}
                        <span className="text-zinc-400 dark:text-zinc-600"> vs 45%</span>
                      </p>
                    </div>
                  </div>

                  {/* Top halal picks */}
                  {sec.top_picks.length > 0 && (
                    <div>
                      <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 dark:text-zinc-500 mb-2">Top Halal Picks</p>
                      <div className="flex flex-wrap gap-1.5">
                        {sec.top_picks.map(pick => (
                          <button
                            key={pick.ticker}
                            onClick={() => onSelectStock?.(pick.ticker)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors"
                            data-testid={`pick-${pick.ticker}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                            <span className="font-semibold">{pick.ticker}</span>
                            <span className="text-zinc-400 dark:text-zinc-500 hidden sm:inline truncate max-w-[100px]">{pick.company_name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
